import { apiError } from "../../utils/formErrors";
import React, { useEffect, useState } from "react";
import { Box, Stack, Dialog, TextField, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BtnPrimary from "../PrimaryButton";
import BtnSecondary from "../SecondaryButton";
import { createGoal, updateGoal } from "../../services/performance";
import { tokens, displayFont } from "../../theme";

/**
 * Setting or editing a goal — the definition half.
 *
 * Shared because two surfaces need it: an admin on someone's profile, and a
 * manager on their team page. One copy so the two cannot drift on which fields
 * exist, which is how weight and the review cycle came to be missing from the
 * only form that had them.
 *
 * Progress is not edited here. That belongs to the person doing the work, on
 * their own goals screen.
 */

const Label = ({ children }) => (
  <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>
    {children}
  </Box>
);

const EMPTY = {
  objective: "",
  description: "",
  weight: 1,
  reviewCycle: "",
  keyResults: [{ title: "", target: "", current: "", unit: "" }],
};

const GoalFormDialog = ({ open, goal, employeeId, cycles = [], onClose, onSaved, notify }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      goal
        ? {
            objective: goal.objective || "",
            description: goal.description || "",
            weight: goal.weight ?? 1,
            reviewCycle: goal.reviewCycle?._id || goal.reviewCycle || "",
            keyResults: goal.keyResults?.length
              ? goal.keyResults.map((k) => ({ ...k }))
              : [{ title: "", target: "", current: "", unit: "" }],
          }
        : EMPTY
    );
  }, [open, goal]);

  const setKr = (i, field, value) =>
    setForm((p) => ({
      ...p,
      keyResults: p.keyResults.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)),
    }));

  const save = async () => {
    if (!form.objective.trim()) {
      notify?.error("An objective is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        objective: form.objective.trim(),
        description: form.description,
        weight: Number(form.weight) || 1,
        reviewCycle: form.reviewCycle || undefined,
        keyResults: form.keyResults
          .filter((k) => k.title?.trim())
          .map((k) => ({
            ...k,
            title: k.title.trim(),
            target: Number(k.target) || 0,
            current: Number(k.current) || 0,
            unit: k.unit,
          })),
      };
      const res = goal
        ? await updateGoal(goal._id, payload)
        : await createGoal({ ...payload, employee: employeeId });
      if (res?.success === false) throw apiError(res, "Could not save the goal");
      notify?.success(goal ? "Goal updated" : "Goal added");
      onSaved?.();
      onClose();
    } catch (err) {
      notify?.error(err?.message || "Could not save the goal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} PaperProps={{ sx: { borderRadius: "12px", width: "min(34rem,94vw)" } }}>
      <Box sx={{ p: "1.5rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>
            {goal ? "Edit goal" : "Add goal"}
          </Box>
          <IconButton aria-label="Close" size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>

        <Label>Objective</Label>
        <TextField
          fullWidth size="small" value={form.objective} placeholder="e.g. Grow region revenue"
          onChange={(e) => setForm((f) => ({ ...f, objective: e.target.value }))}
          sx={{ mb: "1rem" }}
        />

        <Label>Description (optional)</Label>
        <TextField
          fullWidth size="small" multiline minRows={2} value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          sx={{ mb: "1rem" }}
        />

        {/* Weight decides how much this goal moves the appraisal rating; the
            cycle is what freezes it once the cycle starts. */}
        <Stack direction="row" spacing={1.5} sx={{ mb: "1rem" }}>
          <Box sx={{ width: "8rem" }}>
            <Label>Weight</Label>
            <TextField
              fullWidth size="small" type="number" inputProps={{ min: 1 }}
              value={form.weight}
              onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Label>Review cycle (optional)</Label>
            <TextField
              select fullWidth size="small" value={form.reviewCycle}
              onChange={(e) => setForm((f) => ({ ...f, reviewCycle: e.target.value }))}
              SelectProps={{ native: true }}
            >
              <option value="">Not tied to a cycle</option>
              {cycles.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}{c.status && c.status !== "draft" ? ` (${c.status})` : ""}
                </option>
              ))}
            </TextField>
          </Box>
        </Stack>

        <Label>Key results (title · current / target · unit)</Label>
        {form.keyResults.map((k, i) => (
          <Stack key={i} direction="row" spacing={1} sx={{ mb: "0.5rem" }} alignItems="center">
            <TextField size="small" placeholder="Title" value={k.title} onChange={(e) => setKr(i, "title", e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" type="number" placeholder="Cur" value={k.current} onChange={(e) => setKr(i, "current", e.target.value)} sx={{ width: "4rem" }} />
            <TextField size="small" type="number" placeholder="Tgt" value={k.target} onChange={(e) => setKr(i, "target", e.target.value)} sx={{ width: "4rem" }} />
            <TextField size="small" placeholder="Unit" value={k.unit} onChange={(e) => setKr(i, "unit", e.target.value)} sx={{ width: "4.5rem" }} />
            {form.keyResults.length > 1 && (
              <IconButton size="small" onClick={() => setForm((f) => ({ ...f, keyResults: f.keyResults.filter((_, idx) => idx !== i) }))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
        <BtnSecondary
          onClick={() => setForm((f) => ({ ...f, keyResults: [...f.keyResults, { title: "", target: "", current: "", unit: "" }] }))}
          sx={{ height: "2.25rem", px: "1rem", borderRadius: "8px", mb: "1.5rem" }}
        >
          + Add key result
        </BtnSecondary>

        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <BtnSecondary onClick={onClose} disabled={saving} sx={{ borderRadius: "8px" }}>
            Cancel
          </BtnSecondary>
          <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>
            {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save goal"}
          </BtnPrimary>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default GoalFormDialog;
